import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import './App.css';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';

// Configure axios
const API_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_face-harmony-report/artifacts/lf5nom7b_Untitled%20Project.jpg";

// Canvas text wrapping helper
const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0] || '';
  for (let i = 1; i < words.length; i++) {
    const testLine = currentLine + ' ' + words[i];
    if (ctx.measureText(testLine).width > maxWidth) {
      lines.push(currentLine);
      currentLine = words[i];
    } else {
      currentLine = testLine;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Footer Component
const Footer = () => (
  <footer className="text-center py-6 mt-8 border-t border-[rgba(198,167,94,0.1)]">
    <p className="text-[#888888] text-xs">Created by The Manifestation Queen</p>
    <div className="flex justify-center gap-4 mt-3">
      <Link to="/privacy" className="text-[#888888] text-xs hover:text-[#C6A75E] transition-colors">Privacy Policy</Link>
      <Link to="/terms" className="text-[#888888] text-xs hover:text-[#C6A75E] transition-colors">Terms & Conditions</Link>
    </div>
  </footer>
);

// Cookie Notice Component
const CookieNotice = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem('cookiesAccepted');
    if (!accepted) setShow(true);
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-[#1A1A1A] border-t border-[rgba(198,167,94,0.2)] p-4 z-50 animate-slide-up">
      <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-[#888888] text-sm text-center sm:text-left">
          This app uses cookies to improve your experience.
        </p>
        <button onClick={acceptCookies} className="btn-gold text-sm py-2 px-6 whitespace-nowrap">
          Accept
        </button>
      </div>
    </div>
  );
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, agreeToEmails) => {
    const { data } = await axios.post(`${API_URL}/api/auth/register`, { 
      email, 
      password, 
      name,
      agree_to_emails: agreeToEmails
    });
    setUser(data);
    return data;
  };

  const loginWithGoogle = async (sessionId) => {
    const { data } = await axios.post(`${API_URL}/api/auth/google/callback`, { session_id: sessionId });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API_URL}/api/auth/logout`);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, login, register, loginWithGoogle, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Loading Screen
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <div className="text-center">
      <div className="spinner mx-auto mb-4"></div>
      <p className="text-[#888888] text-sm">Loading...</p>
    </div>
  </div>
);

// Logo Component
const Logo = ({ size = 'md' }) => {
  const sizes = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };
  
  return (
    <img 
      src={LOGO_URL}
      alt="The Manifestation Queen"
      className={`${sizes[size]} rounded-full object-cover`}
      data-testid="app-logo"
    />
  );
};

// Background Gradient Component
const GradientBackground = ({ children, className = "" }) => (
  <div className={`min-h-screen bg-gradient-to-b from-black via-[#0A0A0A] to-[#1A1308]/30 ${className}`}>
    {children}
  </div>
);

// Splash Screen
const SplashScreen = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  return (
    <GradientBackground>
      <div className="min-h-screen flex flex-col items-center justify-center p-6 animate-fade-in" data-testid="splash-screen">
        <Logo size="lg" />
        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-gold-gradient mt-8 text-center">
          The External Blueprint
        </h1>
        <p className="text-[#888888] text-center mt-4 max-w-xs font-light">
          Understand what truly suits your face — and step into your natural elegance.
        </p>
      
      <div className="mt-12 space-y-4 w-full max-w-xs">
        <button 
          onClick={() => navigate('/welcome')}
          className="btn-gold w-full"
          data-testid="get-started-btn"
        >
          Get Started
        </button>
        <button 
          onClick={() => navigate('/login')}
          className="btn-outline w-full"
          data-testid="login-btn"
        >
          Log In
        </button>
      </div>
      
      <footer className="absolute bottom-8 text-[#888888] text-xs">
        Created by The Manifestation Queen
      </footer>
    </div>
    </GradientBackground>
  );
};

// Welcome Screen
const WelcomeScreen = () => {
  const navigate = useNavigate();

  const benefits = [
    "Understand your unique facial structure",
    "Receive personalized enhancement guidance",
    "Unlock your natural elegance and presence",
    "Reveal your unique archetype — a reflection of how you are experienced and how to step into it fully"
  ];

  return (
    <GradientBackground>
      <div className="min-h-screen flex flex-col p-6 safe-area-top safe-area-bottom" data-testid="welcome-screen">
        <div className="flex-1 flex flex-col items-center justify-center">
        <Logo size="md" />
        
        <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#F5F5F5] mt-8 text-center">
          Welcome to Your Journey
        </h1>
        
        <p className="text-[#888888] text-center mt-4 max-w-sm">
          The External Blueprint reveals what naturally works for you — so you can refine your look, your presence, and how you show up.
        </p>

        <div className="mt-10 space-y-4 w-full max-w-sm">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 animate-slide-up"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <span className="text-[#C6A75E] text-lg mt-0.5">&#10022;</span>
              <p className="text-[#F5F5F5] text-sm">{benefit}</p>
            </div>
          ))}
        </div>

        <button 
          onClick={() => navigate('/register')}
          className="btn-gold w-full max-w-xs mt-12"
          data-testid="create-blueprint-btn"
        >
          Create My Blueprint
        </button>
        
        <p className="text-[#888888] text-sm mt-4">
          Already have an account?{' '}
          <button 
            onClick={() => navigate('/login')}
            className="text-[#C6A75E] underline"
            data-testid="login-link"
          >
            Log in
          </button>
        </p>
      </div>
    </div>
    </GradientBackground>
  );
};

// Login Screen
const LoginScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col p-6 safe-area-top safe-area-bottom" data-testid="login-screen">
      <button 
        onClick={() => navigate('/')}
        className="text-[#C6A75E] self-start mb-8"
        data-testid="back-btn"
      >
        &#8592; Back
      </button>

      <div className="flex-1 flex flex-col items-center justify-center max-w-sm mx-auto w-full">
        <Logo size="sm" />
        
        <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] mt-6 text-center">
          Welcome Back
        </h1>
        
        <form onSubmit={handleSubmit} className="w-full mt-8 space-y-4">
          {error && (
            <div className="bg-red-900/30 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg text-sm" data-testid="error-message">
              {error}
            </div>
          )}
          
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="luxury-input"
            required
            data-testid="email-input"
          />
          
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="luxury-input"
            required
            data-testid="password-input"
          />
          
          <button 
            type="submit"
            className="btn-gold w-full mt-6"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center w-full my-6">
          <div className="flex-1 h-px bg-[rgba(198,167,94,0.2)]"></div>
          <span className="px-4 text-[#888888] text-sm">or</span>
          <div className="flex-1 h-px bg-[rgba(198,167,94,0.2)]"></div>
        </div>

        {/* Google Sign In */}
        <button 
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + '/auth/callback';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="btn-outline w-full flex items-center justify-center gap-3"
          data-testid="google-login-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#C6A75E" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#C6A75E" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#C6A75E" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#C6A75E" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-[#888888] text-sm mt-6">
          Don't have an account?{' '}
          <button 
            onClick={() => navigate('/register')}
            className="text-[#C6A75E] underline"
            data-testid="register-link"
          >
            Create one
          </button>
        </p>
      </div>
    </div>
  );
};

// Register Screen
const RegisterScreen = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreeToEmails, setAgreeToEmails] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, name, agreeToEmails);
      navigate('/guide');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col p-6 safe-area-top safe-area-bottom" data-testid="register-screen">
      <button 
        onClick={() => navigate('/')}
        className="text-[#C6A75E] self-start mb-8"
        data-testid="back-btn"
      >
        &#8592; Back
      </button>

      <div className="flex-1 flex flex-col items-center justify-center max-w-sm mx-auto w-full">
        <Logo size="sm" />
        
        <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] mt-6 text-center">
          Create Your Account
        </h1>
        
        <form onSubmit={handleSubmit} className="w-full mt-8 space-y-4">
          {error && (
            <div className="bg-red-900/30 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg text-sm" data-testid="error-message">
              {error}
            </div>
          )}
          
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your Name"
            className="luxury-input"
            required
            data-testid="name-input"
          />
          
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="luxury-input"
            required
            data-testid="email-input"
          />
          
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min 6 characters)"
            className="luxury-input"
            minLength={6}
            required
            data-testid="password-input"
          />

          <label className="flex items-start gap-3 cursor-pointer mt-4">
            <input
              type="checkbox"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              className="mt-1 accent-[#C6A75E]"
              required
              data-testid="agree-terms-checkbox"
            />
            <span className="text-[#888888] text-sm">
              I agree to the{' '}
              <Link to="/privacy" className="text-[#C6A75E] underline" target="_blank">Privacy Policy</Link>
              {' '}and{' '}
              <Link to="/terms" className="text-[#C6A75E] underline" target="_blank">Terms & Conditions</Link>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer mt-2">
            <input
              type="checkbox"
              checked={agreeToEmails}
              onChange={(e) => setAgreeToEmails(e.target.checked)}
              className="mt-1 accent-[#C6A75E]"
              data-testid="agree-checkbox"
            />
            <span className="text-[#888888] text-sm">
              I agree to receive my report and updates via email
            </span>
          </label>

          <div className="text-[#888888] text-xs mt-2">
            Your images are processed securely and never used for identification.
          </div>
          
          <button 
            type="submit"
            className="btn-gold w-full mt-6"
            disabled={loading || !agreeToTerms}
            data-testid="submit-btn"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center w-full my-6">
          <div className="flex-1 h-px bg-[rgba(198,167,94,0.2)]"></div>
          <span className="px-4 text-[#888888] text-sm">or</span>
          <div className="flex-1 h-px bg-[rgba(198,167,94,0.2)]"></div>
        </div>

        {/* Google Sign Up */}
        <button 
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + '/auth/callback';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="btn-outline w-full flex items-center justify-center gap-3"
          data-testid="google-register-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#C6A75E" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#C6A75E" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#C6A75E" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#C6A75E" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-[#888888] text-sm mt-6">
          Already have an account?{' '}
          <button 
            onClick={() => navigate('/login')}
            className="text-[#C6A75E] underline"
            data-testid="login-link"
          >
            Log in
          </button>
        </p>
      </div>
    </div>
  );
};

// Photo Guide Screen
const PhotoGuideScreen = () => {
  const navigate = useNavigate();

  const tips = [
    { icon: '&#9788;', text: 'Good natural lighting' },
    { icon: '&#10006;', text: 'No filters or edits' },
    { icon: '&#8635;', text: 'Hair away from face' },
    { icon: '&#9737;', text: 'No sunglasses' },
    { icon: '&#9634;', text: 'Neutral background' },
    { icon: '&#9651;', text: 'Camera at eye level' }
  ];

  return (
    <div className="min-h-screen bg-black flex flex-col p-6 safe-area-top safe-area-bottom" data-testid="guide-screen">
      <button 
        onClick={() => navigate('/dashboard')}
        className="text-[#C6A75E] self-start mb-4"
        data-testid="back-btn"
      >
        &#8592; Back
      </button>

      <div className="flex-1 flex flex-col items-center max-w-sm mx-auto w-full">
        <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] text-center">
          Before You Begin
        </h1>
        
        <p className="text-[#888888] text-center mt-4 text-sm">
          For the most accurate analysis, please follow these guidelines:
        </p>

        <div className="w-full mt-8 space-y-3">
          {tips.map((tip, index) => (
            <div 
              key={index}
              className="luxury-card p-4 flex items-center gap-4 animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <span className="text-[#C6A75E] text-xl" dangerouslySetInnerHTML={{ __html: tip.icon }} />
              <span className="text-[#F5F5F5] text-sm">{tip.text}</span>
            </div>
          ))}
        </div>

        <div className="w-full mt-auto pt-8">
          <button 
            onClick={() => navigate('/upload/front')}
            className="btn-gold w-full"
            data-testid="continue-btn"
          >
            I'm Ready
          </button>
        </div>
      </div>
    </div>
  );
};

// Photo Upload Screen
const PhotoUploadScreen = ({ type, step, nextRoute }) => {
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Reset state when type changes (moving between steps)
  useEffect(() => {
    setPhoto(null);
    setPreview(null);
    setError('');
    setWarnings([]);
    setProcessing(false);
    setLoading(false);
  }, [type]);

  const titles = {
    front: 'Front Facing Photo',
    side: 'Side Profile Photo',
    smile: 'Smiling Photo (Optional)'
  };

  const tips = {
    front: 'Look directly at the camera with a neutral expression',
    side: 'Turn your head 90 degrees to show your profile',
    smile: 'Show us your natural, relaxed smile'
  };

  // Compress image using canvas - mobile optimized
  const processImage = useCallback(async (file) => {
    setProcessing(true);
    setError('');
    
    return new Promise((resolve, reject) => {
      // Create object URL instead of reading full file into memory
      const objectUrl = URL.createObjectURL(file);
      const img = new window.Image();
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 800; // Smaller for mobile
          
          let width = img.width;
          let height = img.height;
          
          // Scale down proportionally
          if (width > height) {
            if (width > MAX_SIZE) {
              height = Math.round((height * MAX_SIZE) / width);
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width = Math.round((width * MAX_SIZE) / height);
              height = MAX_SIZE;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress to JPEG at 60% quality
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.6);
          
          // Clean up
          URL.revokeObjectURL(objectUrl);
          
          setProcessing(false);
          resolve(compressedDataUrl);
        } catch (err) {
          URL.revokeObjectURL(objectUrl);
          setProcessing(false);
          reject(err);
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setProcessing(false);
        reject(new Error('Failed to load image'));
      };
      
      img.src = objectUrl;
    });
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Reset input so same file can be selected again
    e.target.value = '';
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    
    // Check file size (20MB max before compression)
    if (file.size > 20 * 1024 * 1024) {
      setError('Image too large. Please use an image under 20MB.');
      return;
    }
    
    try {
      const compressed = await processImage(file);
      setPreview(compressed);
      setPhoto(compressed);
      setError('');
    } catch (err) {
      console.error('Image processing error:', err);
      setError('Failed to process image. Please try a different photo.');
    }
  };

  const handleUpload = async () => {
    if (!photo && type !== 'smile') {
      setError('Please select a photo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (photo) {
        const { data } = await axios.post(`${API_URL}/api/photos/upload`, {
          photo_type: type,
          image_data: photo
        });
        
        sessionStorage.setItem(`${type}_photo_id`, data.id);
        
        if (data.validation?.warnings?.length) {
          setWarnings(data.validation.warnings);
        }
      }
      
      navigate(nextRoute);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to upload photo');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (type === 'smile') {
      navigate(nextRoute);
    }
  };

  const clearPhoto = () => {
    setPhoto(null);
    setPreview(null);
    setWarnings([]);
    setError('');
  };

  return (
    <div className="min-h-screen bg-black flex flex-col p-6 safe-area-top safe-area-bottom" data-testid={`upload-${type}-screen`}>
      <button 
        onClick={() => navigate(-1)}
        className="text-[#C6A75E] self-start mb-4"
        data-testid="back-btn"
      >
        &#8592; Back
      </button>

      {/* Progress */}
      <div className="flex gap-2 mb-6">
        {[1, 2, 3].map((s) => (
          <div 
            key={s}
            className={`h-1 flex-1 rounded ${s <= step ? 'bg-[#C6A75E]' : 'bg-[#333]'}`}
          />
        ))}
      </div>
      <p className="text-[#888888] text-xs mb-6">Step {step} of 3</p>

      <div className="flex-1 flex flex-col items-center max-w-sm mx-auto w-full">
        <h1 className="font-['Playfair_Display'] text-xl text-[#F5F5F5] text-center">
          {titles[type]}
        </h1>
        
        <p className="text-[#888888] text-center mt-2 text-sm">
          {tips[type]}
        </p>

        {error && (
          <div className="w-full bg-red-900/30 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg text-sm mt-4" data-testid="error-message">
            {error}
          </div>
        )}

        {warnings.length > 0 && (
          <div className="w-full bg-yellow-900/30 border border-yellow-500/50 text-yellow-300 px-4 py-3 rounded-lg text-sm mt-4">
            {warnings.map((w, i) => <p key={i}>{w}</p>)}
          </div>
        )}

        <div className="w-full mt-6">
          {processing ? (
            <div className="upload-zone flex flex-col items-center justify-center py-12">
              <div className="spinner mb-4"></div>
              <p className="text-[#888888] text-sm">Processing image...</p>
            </div>
          ) : preview ? (
            <div className="relative">
              <img 
                src={preview} 
                alt="Preview" 
                className="w-full aspect-[3/4] object-cover rounded-xl border border-[rgba(198,167,94,0.2)]"
                data-testid="photo-preview"
              />
              <button
                onClick={clearPhoto}
                className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm"
                data-testid="retake-btn"
              >
                Retake
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="camera-input"
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="gallery-input"
              />
              
              {/* Take Photo Button */}
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="upload-zone w-full flex flex-col items-center py-6"
                data-testid="take-photo-btn"
              >
                <div className="text-[#C6A75E] text-4xl mb-3">&#128247;</div>
                <p className="text-[#F5F5F5] font-medium">Take Photo</p>
                <p className="text-[#888888] text-xs mt-1">Open camera</p>
              </button>

              {/* Choose from Gallery Button */}
              <button
                onClick={() => galleryInputRef.current?.click()}
                className="upload-zone w-full flex flex-col items-center py-6"
                data-testid="upload-gallery-btn"
              >
                <div className="text-[#C6A75E] text-4xl mb-3">&#128193;</div>
                <p className="text-[#F5F5F5] font-medium">Choose from Gallery</p>
                <p className="text-[#888888] text-xs mt-1">Select existing photo</p>
              </button>

              {/* Tip for camera issues */}
              <p className="text-[#888888] text-xs text-center px-4">
                If camera has issues, take the photo first with your Camera app, then choose from gallery.
              </p>
            </div>
          )}
        </div>

        <div className="w-full mt-auto pt-8 space-y-3">
          <button 
            onClick={handleUpload}
            className="btn-gold w-full"
            disabled={loading || processing || (!photo && type !== 'smile')}
            data-testid="continue-btn"
          >
            {loading ? 'Uploading...' : 'Continue'}
          </button>
          
          {type === 'smile' && (
            <button 
              onClick={handleSkip}
              className="btn-outline w-full"
              data-testid="skip-btn"
            >
              Skip This Step
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Upload Review Screen
const UploadReviewScreen = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState({ front: null, side: null, smile: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPhotos = async () => {
      const frontId = sessionStorage.getItem('front_photo_id');
      const sideId = sessionStorage.getItem('side_photo_id');
      const smileId = sessionStorage.getItem('smile_photo_id');

      const loaded = { front: null, side: null, smile: null };

      try {
        if (frontId) {
          const { data } = await axios.get(`${API_URL}/api/photos/${frontId}`);
          loaded.front = `data:image/jpeg;base64,${data.image_data}`;
        }
        if (sideId) {
          const { data } = await axios.get(`${API_URL}/api/photos/${sideId}`);
          loaded.side = `data:image/jpeg;base64,${data.image_data}`;
        }
        if (smileId) {
          const { data } = await axios.get(`${API_URL}/api/photos/${smileId}`);
          loaded.smile = `data:image/jpeg;base64,${data.image_data}`;
        }
      } catch (err) {
        console.error('Error loading photos:', err);
      }

      setPhotos(loaded);
      setLoading(false);
    };

    loadPhotos();
  }, []);

  const handleSubmit = async () => {
    const frontId = sessionStorage.getItem('front_photo_id');
    const sideId = sessionStorage.getItem('side_photo_id');
    const smileId = sessionStorage.getItem('smile_photo_id');

    if (!frontId || !sideId) {
      toast.error('Please upload required photos first');
      return;
    }

    navigate('/processing', { state: { frontId, sideId, smileId } });
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col p-6 safe-area-top safe-area-bottom" data-testid="review-screen">
      <button 
        onClick={() => navigate(-1)}
        className="text-[#C6A75E] self-start mb-4"
        data-testid="back-btn"
      >
        &#8592; Back
      </button>

      <div className="flex-1 flex flex-col items-center max-w-sm mx-auto w-full">
        <h1 className="font-['Playfair_Display'] text-xl text-[#F5F5F5] text-center">
          Review Your Photos
        </h1>
        
        <p className="text-[#888888] text-center mt-2 text-sm">
          Make sure your photos are clear and well-lit
        </p>

        <div className="grid grid-cols-3 gap-3 mt-8 w-full">
          {['front', 'side', 'smile'].map((type) => (
            <div key={type} className="relative">
              {photos[type] ? (
                <img 
                  src={photos[type]} 
                  alt={type} 
                  className="w-full aspect-square object-cover rounded-lg border border-[rgba(198,167,94,0.2)]"
                  data-testid={`${type}-thumbnail`}
                />
              ) : (
                <div className="w-full aspect-square bg-[#1A1A1A] rounded-lg border border-[rgba(198,167,94,0.2)] flex items-center justify-center">
                  <span className="text-[#888888] text-xs">{type === 'smile' ? 'Optional' : 'Missing'}</span>
                </div>
              )}
              <p className="text-[#888888] text-xs text-center mt-2 capitalize">{type}</p>
            </div>
          ))}
        </div>

        <div className="w-full mt-auto pt-8 space-y-3">
          <button 
            onClick={handleSubmit}
            className="btn-gold w-full"
            disabled={!photos.front || !photos.side}
            data-testid="create-blueprint-btn"
          >
            Create My Blueprint
          </button>
          
          <button 
            onClick={() => navigate('/guide')}
            className="btn-outline w-full"
            data-testid="retake-btn"
          >
            Retake Photos
          </button>
        </div>
      </div>
    </div>
  );
};

// Processing Screen
const ProcessingScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('Creating your External Blueprint...');
  const [error, setError] = useState('');

  useEffect(() => {
    const messages = [
      'Creating your External Blueprint...',
      'Analyzing facial structure...',
      'Identifying key features...',
      'Generating personalized insights...',
      'Preparing your report...'
    ];
    
    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setStatus(messages[messageIndex]);
    }, 3000);

    const createReport = async () => {
      const { frontId, sideId, smileId } = location.state || {};
      
      if (!frontId || !sideId) {
        setError('Missing required photos');
        clearInterval(messageInterval);
        return;
      }

      try {
        const { data } = await axios.post(`${API_URL}/api/reports/create`, {
          front_photo_id: frontId,
          side_photo_id: sideId,
          smile_photo_id: smileId || null
        });

        clearInterval(messageInterval);
        
        // Clear session storage
        sessionStorage.removeItem('front_photo_id');
        sessionStorage.removeItem('side_photo_id');
        sessionStorage.removeItem('smile_photo_id');

        navigate(`/report/${data.id}/preview`);
      } catch (err) {
        clearInterval(messageInterval);
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Failed to create report');
      }
    };

    createReport();

    return () => clearInterval(messageInterval);
  }, [location.state, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="processing-error">
        <div className="text-red-400 text-lg mb-4">Something went wrong</div>
        <p className="text-[#888888] text-sm mb-8">{error}</p>
        <button onClick={() => navigate('/guide')} className="btn-gold">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="processing-screen">
      <Logo size="md" />
      
      <div className="mt-8 animate-pulse-glow w-20 h-20 rounded-full border-2 border-[#C6A75E] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
      
      <h2 className="font-['Playfair_Display'] text-xl text-[#F5F5F5] mt-8 text-center animate-fade-in">
        {status}
      </h2>
      
      <p className="text-[#888888] text-sm mt-4 text-center max-w-xs">
        This may take a moment. Please don't close this page.
      </p>
    </div>
  );
};

// Report Preview Screen (Free Preview + Paywall)
const ReportPreviewScreen = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const loadReport = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/reports/${reportId}`);
        setReport(data);
        
        if (data.is_paid) {
          navigate(`/report/${reportId}`, { replace: true });
        }
      } catch (err) {
        toast.error('Failed to load report');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [reportId, navigate]);

  const handleUnlock = async () => {
    setPaymentLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/payments/create-checkout`, {
        report_id: reportId,
        origin_url: window.location.origin
      });
      
      window.location.href = data.url;
    } catch (err) {
      toast.error('Failed to start payment');
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const preview = report?.preview || {};

  return (
    <div className="min-h-screen bg-black flex flex-col p-6 safe-area-top safe-area-bottom" data-testid="preview-screen">
      <div className="flex-1 flex flex-col items-center max-w-md mx-auto w-full">
        <Logo size="sm" />
        
        <h1 className="font-['Playfair_Display'] text-2xl text-gold-gradient mt-6 text-center">
          Your Blueprint Is Ready
        </h1>
        
        <p className="text-[#888888] text-sm mt-2">Free Preview</p>

        {/* Free Preview Content */}
        <div className="w-full mt-8 space-y-4">
          <div className="luxury-card p-6 animate-slide-up">
            <h3 className="text-[#C6A75E] text-sm uppercase tracking-wider mb-3">Face Shape</h3>
            <p className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] capitalize">
              {preview.face_shape || 'Analyzing...'}
            </p>
            <p className="text-[#888888] text-sm mt-2">
              {preview.face_shape_description || ''}
            </p>
          </div>

          <div className="luxury-card p-6 animate-slide-up stagger-2">
            <h3 className="text-[#C6A75E] text-sm uppercase tracking-wider mb-3">Summary</h3>
            <p className="text-[#F5F5F5] text-sm leading-relaxed">
              {preview.summary_teaser || ''}
            </p>
          </div>

          {/* One free feature insight */}
          {preview.feature_teaser && (
            <div className="luxury-card p-6 animate-slide-up stagger-3">
              <h3 className="text-[#C6A75E] text-sm uppercase tracking-wider mb-3">Feature Highlight</h3>
              <p className="text-[#C6A75E] font-medium capitalize mb-2">{preview.feature_teaser.name?.replace(/_/g, ' ')}</p>
              <p className="text-[#F5F5F5] text-sm">{preview.feature_teaser.observation}</p>
            </div>
          )}
        </div>

        {/* Locked Content Teaser */}
        <div className="w-full mt-6 relative">
          <div className="content-locked">
            <div className="luxury-card p-6">
              <h3 className="text-[#C6A75E] text-sm mb-3">Feature Breakdown</h3>
              <p className="text-[#F5F5F5]">Your complete analysis...</p>
            </div>
            <div className="luxury-card p-6 mt-4">
              <h3 className="text-[#C6A75E] text-sm mb-3">Enhancement Blueprint</h3>
              <p className="text-[#F5F5F5]">Personalized recommendations...</p>
            </div>
          </div>
          
          {/* Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
            <div className="text-center p-6">
              <p className="text-[#F5F5F5] font-['Playfair_Display'] text-lg mb-2">
                Your full External Blueprint is ready to unlock.
              </p>
              <p className="text-[#888888] text-sm">
                Complete analysis, enhancement blueprint, and PDF download
              </p>
            </div>
          </div>
        </div>

        {/* Payment CTA */}
        <div className="w-full mt-8 space-y-4">
          <button 
            onClick={handleUnlock}
            className="btn-gold w-full"
            disabled={paymentLoading}
            data-testid="unlock-btn"
          >
            {paymentLoading ? 'Processing...' : 'Unlock Full Report - €29'}
          </button>
          
          <p className="text-[#888888] text-xs text-center">
            Secure payment via Stripe. Includes PDF download.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Use params hook
const { useParams } = require('react-router-dom');

// Payment Success Screen
const PaymentSuccessScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [reportId, setReportId] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      if (attempts >= 10) {
        setStatus('timeout');
        return;
      }

      try {
        const { data } = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          // Find the report from the user's reports
          const reportsRes = await axios.get(`${API_URL}/api/reports`);
          const paidReport = reportsRes.data.find(r => r.is_paid);
          if (paidReport) {
            setReportId(paidReport.id);
          }
        } else if (data.status === 'expired') {
          setStatus('failed');
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (err) {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    };

    pollStatus();
  }, [searchParams, navigate]);

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
        <div className="spinner mb-4"></div>
        <p className="text-[#F5F5F5]">Verifying payment...</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="payment-success">
        <div className="text-[#C6A75E] text-6xl mb-6">&#10003;</div>
        <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] mb-4">Payment Successful</h1>
        <p className="text-[#888888] text-center mb-8">Your full External Blueprint is now unlocked.</p>
        
        <button 
          onClick={() => navigate(reportId ? `/report/${reportId}` : '/dashboard')}
          className="btn-gold"
          data-testid="view-report-btn"
        >
          View Full Report
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
      <div className="text-red-400 text-6xl mb-6">&#10007;</div>
      <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] mb-4">Payment Issue</h1>
      <p className="text-[#888888] text-center mb-8">There was an issue with your payment. Please try again.</p>
      
      <button onClick={() => navigate('/dashboard')} className="btn-gold">
        Go to Dashboard
      </button>
    </div>
  );
};

// Full Report Screen
const FullReportScreen = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const shareCanvasRef = useRef(null);

  useEffect(() => {
    const loadReport = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/reports/${reportId}`);
        if (!data.is_paid) {
          navigate(`/report/${reportId}/preview`, { replace: true });
          return;
        }
        setReport(data);
      } catch (err) {
        toast.error('Failed to load report');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [reportId, navigate]);

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/${reportId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `external-blueprint-${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded!');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const generateShareCard = () => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const w = 1080;
      const h = 1350;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');

      // Background gradient
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#000000');
      bg.addColorStop(0.6, '#0A0A0A');
      bg.addColorStop(1, '#1A1308');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Gold accent line at top
      ctx.fillStyle = '#C6A75E';
      ctx.fillRect(w / 2 - 40, 60, 80, 2);

      // Brand name
      ctx.fillStyle = '#C6A75E';
      ctx.font = '600 22px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText('THE EXTERNAL BLUEPRINT', w / 2, 110);

      // Subtitle
      ctx.fillStyle = '#888888';
      ctx.font = '16px Arial, sans-serif';
      ctx.fillText('by The Manifestation Queen', w / 2, 145);

      // Divider
      ctx.fillStyle = 'rgba(198, 167, 94, 0.3)';
      ctx.fillRect(w / 2 - 100, 185, 200, 1);

      // Face shape label
      ctx.fillStyle = '#C6A75E';
      ctx.font = '14px Arial, sans-serif';
      ctx.letterSpacing = '4px';
      ctx.fillText('FACE SHAPE', w / 2, 240);

      // Face shape value
      const faceShape = (report?.preview?.face_shape || report?.full_report?.face_structure?.face_shape || '').toUpperCase();
      ctx.fillStyle = '#F5F5F5';
      ctx.font = '600 52px Georgia, serif';
      ctx.fillText(faceShape, w / 2, 310);

      // Archetype name
      const archetype = report?.full_report?.archetype?.name || report?.preview?.archetype_name || '';
      if (archetype) {
        ctx.fillStyle = '#C6A75E';
        ctx.font = 'italic 22px Georgia, serif';
        ctx.fillText(archetype, w / 2, 355);
      }

      // Face shape description
      const desc = report?.preview?.face_shape_description || report?.full_report?.face_structure?.face_shape_description || '';
      if (desc) {
        ctx.fillStyle = '#888888';
        ctx.font = '16px Arial, sans-serif';
        const descLines = wrapText(ctx, desc, w - 160);
        descLines.slice(0, 3).forEach((line, i) => {
          ctx.fillText(line, w / 2, 370 + i * 26);
        });
      }

      // Divider
      const summaryY = 470;
      ctx.fillStyle = 'rgba(198, 167, 94, 0.2)';
      ctx.fillRect(80, summaryY, w - 160, 1);

      // Executive summary snippet
      const summary = report?.full_report?.executive_summary || '';
      if (summary) {
        ctx.fillStyle = '#F5F5F5';
        ctx.font = 'italic 18px Georgia, serif';
        const summaryLines = wrapText(ctx, summary.substring(0, 200) + (summary.length > 200 ? '...' : ''), w - 160);
        summaryLines.slice(0, 5).forEach((line, i) => {
          ctx.fillText(line, w / 2, summaryY + 50 + i * 30);
        });
      }

      // Signature sentence
      const sig = report?.full_report?.signature_sentence || '';
      if (sig) {
        const sigY = summaryY + 230;
        ctx.fillStyle = '#C6A75E';
        ctx.font = 'italic 20px Georgia, serif';
        const sigLines = wrapText(ctx, sig, w - 140);
        sigLines.slice(0, 3).forEach((line, i) => {
          ctx.fillText(line, w / 2, sigY + i * 32);
        });
      }

      // Card border (gold outline rect)
      ctx.strokeStyle = 'rgba(198, 167, 94, 0.15)';
      ctx.lineWidth = 1;
      ctx.strokeRect(40, 40, w - 80, h - 80);

      // Inner decorative corners
      ctx.strokeStyle = '#C6A75E';
      ctx.lineWidth = 2;
      // Top-left
      ctx.beginPath(); ctx.moveTo(60, 90); ctx.lineTo(60, 60); ctx.lineTo(90, 60); ctx.stroke();
      // Top-right
      ctx.beginPath(); ctx.moveTo(w - 90, 60); ctx.lineTo(w - 60, 60); ctx.lineTo(w - 60, 90); ctx.stroke();
      // Bottom-left
      ctx.beginPath(); ctx.moveTo(60, h - 90); ctx.lineTo(60, h - 60); ctx.lineTo(90, h - 60); ctx.stroke();
      // Bottom-right
      ctx.beginPath(); ctx.moveTo(w - 90, h - 60); ctx.lineTo(w - 60, h - 60); ctx.lineTo(w - 60, h - 90); ctx.stroke();

      // Footer
      ctx.fillStyle = 'rgba(198, 167, 94, 0.3)';
      ctx.fillRect(w / 2 - 100, h - 130, 200, 1);
      ctx.fillStyle = '#888888';
      ctx.font = '14px Arial, sans-serif';
      ctx.fillText('themanifestationqueen.com', w / 2, h - 90);

      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const blob = await generateShareCard();
      const file = new File([blob], 'my-external-blueprint.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: 'My External Blueprint',
          text: `My face shape is ${report?.preview?.face_shape || 'unique'}. Discover yours at The External Blueprint.`,
          files: [file]
        });
      } else if (navigator.share) {
        await navigator.share({
          title: 'My External Blueprint',
          text: `My face shape is ${report?.preview?.face_shape || 'unique'}. Discover yours at The External Blueprint by The Manifestation Queen.`,
          url: window.location.origin
        });
      } else {
        // Fallback: download the image
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'my-external-blueprint.png';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        toast.success('Share card downloaded! Share it on your socials.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Could not share. Card has been downloaded instead.');
        try {
          const blob = await generateShareCard();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'my-external-blueprint.png';
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
        } catch (e) { /* silent */ }
      }
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const fullReport = report?.full_report || {};

  return (
    <div className="min-h-screen bg-black pb-8 safe-area-bottom" data-testid="full-report-screen">
      {/* Header */}
      <div className="sticky top-0 bg-black/90 backdrop-blur-sm border-b border-[rgba(198,167,94,0.2)] p-4 z-10">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <button onClick={() => navigate('/dashboard')} className="text-[#C6A75E]" data-testid="back-btn">
            &#8592; Dashboard
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={handleShare}
              className="text-[#C6A75E] text-sm flex items-center gap-1"
              disabled={sharing}
              data-testid="share-btn"
            >
              {sharing ? 'Creating...' : 'Share'}
            </button>
            <button onClick={handleDownloadPDF} className="text-[#C6A75E] text-sm" data-testid="download-btn">
              Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-2xl mx-auto">
        {/* Title */}
        <div className="text-center mb-8">
          <Logo size="sm" />
          <h1 className="font-['Playfair_Display'] text-2xl text-gold-gradient mt-4">
            Your External Blueprint
          </h1>
          <p className="text-[#888888] text-sm mt-2">
            {new Date(report?.created_at).toLocaleDateString('en-US', { 
              year: 'numeric', month: 'long', day: 'numeric' 
            })}
          </p>
        </div>

        {/* Executive Summary */}
        <section className="report-section animate-slide-up">
          <h3>Executive Summary</h3>
          <p className="text-[#F5F5F5] leading-relaxed">
            {fullReport.executive_summary}
          </p>
          {fullReport.signature_sentence && (
            <p className="text-[#C6A75E] font-['Playfair_Display'] text-base italic text-center mt-6 leading-relaxed" data-testid="signature-sentence">
              {fullReport.signature_sentence}
            </p>
          )}
        </section>

        {/* Core Essence */}
        {fullReport.core_essence && (
          <section className="report-section animate-slide-up" data-testid="core-essence-section">
            <h3>Core Essence</h3>
            <div className="space-y-4">
              {fullReport.core_essence.softness_vs_structure && (
                <div>
                  <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Softness vs Structure</p>
                  <p className="text-[#F5F5F5] text-sm leading-relaxed">{fullReport.core_essence.softness_vs_structure}</p>
                </div>
              )}
              {fullReport.core_essence.visual_tone && (
                <div>
                  <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Visual Tone</p>
                  <p className="text-[#F5F5F5] text-sm leading-relaxed">{fullReport.core_essence.visual_tone}</p>
                </div>
              )}
              {fullReport.core_essence.overall_presence && (
                <div>
                  <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Overall Presence</p>
                  <p className="text-[#F5F5F5] text-sm leading-relaxed">{fullReport.core_essence.overall_presence}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Archetype */}
        {fullReport.archetype && (
          <section className="report-section animate-slide-up" data-testid="archetype-section">
            <h3>Your Archetype</h3>
            <p className="font-['Playfair_Display'] text-xl text-[#C6A75E] text-center mb-4" data-testid="archetype-name">
              {fullReport.archetype.name}
            </p>
            {fullReport.archetype.description && (
              <div className="text-[#F5F5F5] text-sm leading-relaxed whitespace-pre-line mb-4">
                {fullReport.archetype.description}
              </div>
            )}
            {fullReport.archetype.alignment_insight && (
              <div className="mb-4">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Alignment Insight</p>
                <p className="text-[#F5F5F5] text-sm leading-relaxed">{fullReport.archetype.alignment_insight}</p>
              </div>
            )}
            {fullReport.archetype.external_embodiment?.length > 0 && (
              <div className="mb-4">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">External Embodiment</p>
                <ul className="space-y-1">
                  {fullReport.archetype.external_embodiment.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#C6A75E] text-xs mt-1">&#10022;</span>
                      <span className="text-[#F5F5F5] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {fullReport.archetype.internal_embodiment?.length > 0 && (
              <div className="mb-4">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">Internal Embodiment</p>
                <ul className="space-y-1">
                  {fullReport.archetype.internal_embodiment.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#C6A75E] text-xs mt-1">&#10022;</span>
                      <span className="text-[#F5F5F5] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {fullReport.archetype.activation && (
              <div className="p-4 bg-[rgba(198,167,94,0.08)] rounded-lg border border-[rgba(198,167,94,0.15)]">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Activation</p>
                <p className="text-[#F5F5F5] text-sm leading-relaxed">{fullReport.archetype.activation}</p>
              </div>
            )}
          </section>
        )}

        {/* Face Structure */}
        <section className="report-section animate-slide-up stagger-1">
          <h3>Face Structure</h3>
          <div className="space-y-4">
            <div>
              <p className="text-[#888888] text-sm">Face Shape</p>
              <p className="text-[#F5F5F5] text-lg capitalize">{fullReport.face_structure?.face_shape}</p>
              <p className="text-[#888888] text-sm mt-1">{fullReport.face_structure?.face_shape_description}</p>
            </div>
            <div className="gold-divider"></div>
            <div>
              <p className="text-[#888888] text-sm">Symmetry</p>
              <p className="text-[#F5F5F5] capitalize">{fullReport.face_structure?.symmetry?.replace(/_/g, ' ')}</p>
              <p className="text-[#888888] text-sm mt-1">{fullReport.face_structure?.symmetry_note}</p>
            </div>
            
            {/* Enhanced Face Analysis */}
            {fullReport.face_structure?.face_analysis && (
              <>
                <div className="gold-divider"></div>
                <div>
                  <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-3">Facial Thirds</p>
                  {fullReport.face_structure.face_analysis.facial_thirds && Object.entries(fullReport.face_structure.face_analysis.facial_thirds).map(([key, value]) => (
                    <div key={key} className="mb-2">
                      <span className="text-[#C6A75E] text-sm capitalize">{key}: </span>
                      <span className="text-[#F5F5F5] text-sm">{value}</span>
                    </div>
                  ))}
                </div>
                {fullReport.face_structure.face_analysis.width_length_balance && (
                  <div>
                    <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Width-Length Balance</p>
                    <p className="text-[#F5F5F5] text-sm">{fullReport.face_structure.face_analysis.width_length_balance}</p>
                  </div>
                )}
                {fullReport.face_structure.face_analysis.feature_spacing && (
                  <div>
                    <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Feature Spacing</p>
                    <p className="text-[#F5F5F5] text-sm">{fullReport.face_structure.face_analysis.feature_spacing}</p>
                  </div>
                )}
                {fullReport.face_structure.face_analysis.softness_vs_definition && (
                  <div>
                    <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Softness vs Definition</p>
                    <p className="text-[#F5F5F5] text-sm">{fullReport.face_structure.face_analysis.softness_vs_definition}</p>
                  </div>
                )}
                {fullReport.face_structure.face_analysis.contrast_level && (
                  <div>
                    <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Contrast Level</p>
                    <p className="text-[#F5F5F5] text-sm">{fullReport.face_structure.face_analysis.contrast_level}</p>
                  </div>
                )}
              </>
            )}
            
            {/* Legacy facial_balance fallback */}
            {!fullReport.face_structure?.face_analysis && fullReport.face_structure?.facial_balance && (
              <>
                <div className="gold-divider"></div>
                <div>
                  <p className="text-[#888888] text-sm mb-2">Facial Balance</p>
                  {Object.entries(fullReport.face_structure.facial_balance).map(([key, value]) => (
                    <div key={key} className="mb-2">
                      <span className="text-[#C6A75E] text-sm capitalize">{key.replace(/_/g, ' ')}: </span>
                      <span className="text-[#F5F5F5] text-sm">{value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Feature Breakdown */}
        <section className="report-section animate-slide-up stagger-2">
          <h3>Feature Breakdown</h3>
          <div className="space-y-6">
            {fullReport.feature_breakdown && Object.entries(fullReport.feature_breakdown).map(([feature, data]) => (
              <div key={feature}>
                <p className="text-[#C6A75E] font-medium capitalize mb-2">{feature.replace(/_/g, ' ')}</p>
                <div className="pl-4 border-l border-[rgba(198,167,94,0.3)] space-y-2">
                  <p className="text-[#F5F5F5] text-sm"><span className="text-[#888888]">Observation:</span> {data?.observation}</p>
                  {data?.contribution && (
                    <p className="text-[#F5F5F5] text-sm"><span className="text-[#888888]">Contribution:</span> {data?.contribution}</p>
                  )}
                  <p className="text-[#F5F5F5] text-sm"><span className="text-[#888888]">Strength:</span> {data?.strength}</p>
                  <p className="text-[#F5F5F5] text-sm"><span className="text-[#888888]">Enhancement:</span> {data?.enhancement}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Enhancement Blueprint */}
        <section className="report-section animate-slide-up stagger-3">
          <h3>Enhancement Blueprint</h3>
          <div className="space-y-4">
            {/* Focus First */}
            {fullReport.enhancement_blueprint?.focus_first?.length > 0 && (
              <div>
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">Focus First</p>
                <ul className="space-y-2">
                  {fullReport.enhancement_blueprint.focus_first.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#C6A75E]">&#10022;</span>
                      <span className="text-[#F5F5F5] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Avoid */}
            {fullReport.enhancement_blueprint?.avoid?.length > 0 && (
              <div>
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">Avoid</p>
                <ul className="space-y-2">
                  {fullReport.enhancement_blueprint.avoid.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#888888]">&#10005;</span>
                      <span className="text-[#F5F5F5] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Quick Wins */}
            {fullReport.enhancement_blueprint?.quick_wins?.length > 0 && (
              <div className="p-4 bg-[rgba(198,167,94,0.08)] rounded-lg border border-[rgba(198,167,94,0.15)]">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">Quick Wins</p>
                <ul className="space-y-2">
                  {fullReport.enhancement_blueprint.quick_wins.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#C6A75E]">&#10148;</span>
                      <span className="text-[#F5F5F5] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="gold-divider"></div>

            {fullReport.enhancement_blueprint?.hairstyles?.length > 0 && (
              <div>
                <p className="text-[#888888] text-sm mb-2">Recommended Hairstyles</p>
                <div className="flex flex-wrap gap-2">
                  {fullReport.enhancement_blueprint.hairstyles.map((style, i) => (
                    <span key={i} className="bg-[rgba(198,167,94,0.1)] border border-[rgba(198,167,94,0.3)] px-3 py-1 rounded-full text-sm text-[#F5F5F5]">
                      {style}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {fullReport.enhancement_blueprint?.makeup_focus?.length > 0 && (
              <div>
                <p className="text-[#888888] text-sm mb-2">Makeup Focus</p>
                <ul className="list-disc list-inside text-[#F5F5F5] text-sm space-y-1">
                  {fullReport.enhancement_blueprint.makeup_focus.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {fullReport.enhancement_blueprint?.styling_direction && (
              <div>
                <p className="text-[#888888] text-sm mb-1">Styling Direction</p>
                <p className="text-[#F5F5F5] text-sm">{fullReport.enhancement_blueprint.styling_direction}</p>
              </div>
            )}
          </div>
        </section>

        {/* Presence Reflection */}
        <section className="report-section animate-slide-up stagger-4">
          <h3>Presence Reflection</h3>
          <p className="text-[#F5F5F5] leading-relaxed italic">
            "{fullReport.presence_reflection}"
          </p>
        </section>

        {/* Evolution System (for evolution reports) */}
        {fullReport.evolution && (
          <section className="report-section animate-slide-up" data-testid="evolution-section">
            <h3>This Month's Focus</h3>
            <p className="text-[#C6A75E] font-['Playfair_Display'] text-lg text-center mb-4">
              {fullReport.evolution.theme}
            </p>
            <p className="text-[#888888] text-xs text-center mb-6">
              Month {fullReport.evolution.month_number} of your evolution
            </p>

            {fullReport.evolution.what_stays_true && (
              <div className="mb-5">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">What Stays True</p>
                <p className="text-[#F5F5F5] text-sm leading-relaxed">{fullReport.evolution.what_stays_true}</p>
              </div>
            )}

            {fullReport.evolution.this_months_insight && (
              <div className="mb-5">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">This Month's Insight</p>
                <p className="text-[#F5F5F5] text-sm leading-relaxed">{fullReport.evolution.this_months_insight}</p>
              </div>
            )}

            {fullReport.evolution.practical_refinements?.length > 0 && (
              <div className="mb-5">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">Practical Refinements</p>
                <ul className="space-y-2">
                  {fullReport.evolution.practical_refinements.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#C6A75E]">&#10022;</span>
                      <span className="text-[#F5F5F5] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {fullReport.evolution.avoid_this_month?.length > 0 && (
              <div className="mb-5">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">Avoid This Month</p>
                <ul className="space-y-2">
                  {fullReport.evolution.avoid_this_month.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#888888]">&#10005;</span>
                      <span className="text-[#F5F5F5] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {fullReport.evolution.quick_win && (
              <div className="p-4 bg-[rgba(198,167,94,0.08)] rounded-lg border border-[rgba(198,167,94,0.15)] mb-5">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">Quick Win</p>
                <p className="text-[#F5F5F5] text-sm">{fullReport.evolution.quick_win}</p>
              </div>
            )}

            {fullReport.evolution.progress_note && (
              <p className="text-[#C6A75E] font-['Playfair_Display'] text-sm italic text-center mt-4 leading-relaxed">
                {fullReport.evolution.progress_note}
              </p>
            )}
          </section>
        )}

        {/* Embodiment */}
        <section className="report-section animate-slide-up stagger-5">
          <h3>Embodiment</h3>
          <div className="space-y-6">
            <div>
              <p className="text-[#888888] text-sm mb-3">Journaling Prompts</p>
              <ul className="space-y-2">
                {fullReport.embodiment?.journaling_prompts?.map((prompt, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#C6A75E]">&#10022;</span>
                    <span className="text-[#F5F5F5] text-sm italic">{prompt}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Affirmation */}
            {fullReport.embodiment?.affirmation && (
              <div className="text-center py-4">
                <p className="text-[#888888] text-xs uppercase tracking-wider mb-2">Affirmation</p>
                <p className="text-[#C6A75E] font-['Playfair_Display'] text-lg leading-relaxed">
                  "{fullReport.embodiment.affirmation}"
                </p>
              </div>
            )}

            {/* Visualization */}
            {fullReport.embodiment?.visualization && (
              <div>
                <p className="text-[#888888] text-sm mb-2">Visualization</p>
                <p className="text-[#F5F5F5] text-sm leading-relaxed italic">{fullReport.embodiment.visualization}</p>
              </div>
            )}
            
            {/* Action Step */}
            {fullReport.embodiment?.action_step && (
              <div className="p-4 bg-[rgba(198,167,94,0.08)] rounded-lg border border-[rgba(198,167,94,0.15)]">
                <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-1">This Week's Action</p>
                <p className="text-[#F5F5F5] text-sm">{fullReport.embodiment.action_step}</p>
              </div>
            )}

            {/* Legacy identity_shift fallback */}
            {!fullReport.embodiment?.affirmation && fullReport.embodiment?.identity_shift && (
              <div>
                <p className="text-[#888888] text-sm mb-2">Identity Shift</p>
                <p className="text-[#F5F5F5] font-['Playfair_Display'] text-lg">
                  "{fullReport.embodiment.identity_shift}"
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-[#111] rounded-lg">
          <p className="text-[#888888] text-xs leading-relaxed">
            {fullReport.disclaimer}
          </p>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

// Dashboard Screen
const DashboardScreen = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [reportsRes, subRes] = await Promise.all([
          axios.get(`${API_URL}/api/reports`),
          axios.get(`${API_URL}/api/subscriptions/my`)
        ]);
        setReports(reportsRes.data);
        setSubscription(subRes.data);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSubscribe = async () => {
    setSubscriptionLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscriptions/create-checkout`, {
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error('Failed to start subscription. Please try again.');
      setSubscriptionLoading(false);
    }
  };

  const handleDeleteReport = async (reportId, e) => {
    e.stopPropagation(); // Prevent navigation
    
    if (!window.confirm('Are you sure you want to delete this blueprint? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/reports/${reportId}`);
      setReports(reports.filter(r => r.id !== reportId));
      toast.success('Blueprint deleted');
    } catch (err) {
      toast.error('Failed to delete blueprint');
    }
  };

  return (
    <div className="min-h-screen bg-black pb-8 safe-area-bottom" data-testid="dashboard-screen">
      {/* Header */}
      <div className="bg-[#111] p-6 border-b border-[rgba(198,167,94,0.2)]">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div>
            <p className="text-[#888888] text-sm">Welcome back,</p>
            <p className="text-[#F5F5F5] font-['Playfair_Display'] text-lg">{user?.name || 'Guest'}</p>
          </div>
          <button onClick={handleLogout} className="text-[#C6A75E] text-sm" data-testid="logout-btn">
            Log out
          </button>
        </div>
      </div>

      <div className="p-6 max-w-2xl mx-auto">
        {/* Subscription Banner */}
        {!subscription?.has_subscription && (
          <div className="luxury-card p-6 mb-6 bg-gradient-to-r from-[#1A1A1A] to-[#0F3B2E]/30 border-[#C6A75E]/30">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-[#C6A75E] font-['Playfair_Display'] text-lg">Unlimited Blueprints</p>
                <p className="text-[#888888] text-sm mt-1">Get unlimited access for €19/month</p>
              </div>
              <button 
                onClick={handleSubscribe}
                className="btn-gold text-sm py-3"
                disabled={subscriptionLoading}
                data-testid="subscribe-btn"
              >
                {subscriptionLoading ? 'Loading...' : 'Subscribe'}
              </button>
            </div>
          </div>
        )}

        {subscription?.has_subscription && (
          <div className="luxury-card p-4 mb-6 border-[#C6A75E]/50">
            <div className="flex items-center gap-3">
              <span className="text-[#C6A75E]">&#10022;</span>
              <div>
                <p className="text-[#F5F5F5] text-sm">Active Subscription</p>
                <p className="text-[#888888] text-xs">
                  Expires: {new Date(subscription.expires_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Create New */}
        <button 
          onClick={() => navigate('/guide')}
          className="w-full luxury-card p-6 flex items-center justify-between mb-8 hover:border-[#C6A75E] transition-all"
          data-testid="new-blueprint-btn"
        >
          <div>
            <p className="text-[#F5F5F5] font-medium">Create Your Next Evolution</p>
            <p className="text-[#888888] text-sm mt-1">Start your self-discovery journey</p>
          </div>
          <span className="text-[#C6A75E] text-2xl">+</span>
        </button>

        {/* Reports */}
        {loading ? (
          <div className="text-center py-8">
            <div className="spinner mx-auto"></div>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#888888]">No blueprints yet</p>
            <p className="text-[#888888] text-sm mt-2">Create your first External Blueprint to get started</p>
          </div>
        ) : (
          <>
            {/* Current Report */}
            {reports.length > 0 && (
              <div className="mb-6">
                <h2 className="text-[#F5F5F5] font-['Playfair_Display'] text-lg mb-4" data-testid="current-report-heading">Current Report</h2>
                <div
                  className="luxury-card p-5 hover:border-[#C6A75E] transition-all border-[#C6A75E]/30"
                  data-testid={`report-${reports[0].id}`}
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => navigate(reports[0].is_paid ? `/report/${reports[0].id}` : `/report/${reports[0].id}/preview`)}
                      className="flex-1 text-left"
                    >
                      <p className="text-[#F5F5F5] font-medium capitalize">
                        {reports[0].preview?.face_shape || 'Blueprint'} Analysis
                        {reports[0].preview?.archetype_name && (
                          <span className="text-[#C6A75E] text-xs ml-2">&#10022; {reports[0].preview.archetype_name}</span>
                        )}
                      </p>
                      <p className="text-[#888888] text-sm mt-1">
                        {new Date(reports[0].created_at).toLocaleDateString()}
                      </p>
                    </button>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {reports[0].is_paid ? (
                          <span className="text-[#C6A75E] text-sm">&#10003; Unlocked</span>
                        ) : (
                          <span className="text-[#888888] text-sm">Preview</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => handleDeleteReport(reports[0].id, e)}
                        className="text-[#888888] hover:text-red-400 p-2 transition-colors"
                        title="Delete"
                        data-testid={`delete-${reports[0].id}`}
                      >
                        &#128465;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Previous Reports */}
            {reports.length > 1 && (
              <div className="mb-6">
                <h2 className="text-[#F5F5F5] font-['Playfair_Display'] text-lg mb-4" data-testid="previous-reports-heading">Previous Reports</h2>
                <div className="space-y-3">
                  {reports.slice(1).map((report) => (
                    <div
                      key={report.id}
                      className="luxury-card p-4 hover:border-[#C6A75E] transition-all"
                      data-testid={`report-${report.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => navigate(report.is_paid ? `/report/${report.id}` : `/report/${report.id}/preview`)}
                          className="flex-1 text-left"
                        >
                          <p className="text-[#F5F5F5] font-medium capitalize text-sm">
                            {report.preview?.face_shape || 'Blueprint'} Analysis
                          </p>
                          <p className="text-[#888888] text-xs mt-1">
                            {new Date(report.created_at).toLocaleDateString()}
                          </p>
                        </button>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            {report.is_paid ? (
                              <span className="text-[#C6A75E] text-xs">&#10003; Unlocked</span>
                            ) : (
                              <span className="text-[#888888] text-xs">Preview</span>
                            )}
                          </div>
                          <button
                            onClick={(e) => handleDeleteReport(report.id, e)}
                            className="text-[#888888] hover:text-red-400 p-1 transition-colors"
                            title="Delete"
                            data-testid={`delete-${report.id}`}
                          >
                            &#128465;
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Member Dashboard Link */}
        {subscription?.has_subscription && (
          <button 
            onClick={() => navigate('/membership')}
            className="w-full mt-4 luxury-card p-4 flex items-center justify-between hover:border-[#C6A75E] transition-all"
            data-testid="membership-link"
          >
            <div className="flex items-center gap-3">
              <span className="text-[#C6A75E] text-xl">&#10022;</span>
              <div>
                <p className="text-[#F5F5F5] font-medium">Member Dashboard</p>
                <p className="text-[#888888] text-xs">Access your membership benefits</p>
              </div>
            </div>
            <span className="text-[#C6A75E]">&#8594;</span>
          </button>
        )}

        {/* Admin Link */}
        {user?.role === 'admin' && (
          <button 
            onClick={() => navigate('/admin')}
            className="w-full mt-8 text-[#C6A75E] text-sm underline"
            data-testid="admin-link"
          >
            Admin Panel
          </button>
        )}

        {/* Delete Account */}
        <div className="mt-12 pt-6 border-t border-[rgba(198,167,94,0.1)]">
          <button
            onClick={async () => {
              if (!window.confirm('Are you sure you want to delete your account and all data? This action cannot be undone.')) return;
              if (!window.confirm('This will permanently delete all your blueprints, photos, and personal data. Continue?')) return;
              try {
                await axios.delete(`${API_URL}/api/user/data`);
                await logout();
                navigate('/');
                toast.success('Your account and data have been deleted.');
              } catch (err) {
                toast.error('Failed to delete account. Please try again.');
              }
            }}
            className="text-red-400/60 text-xs hover:text-red-400 transition-colors"
            data-testid="delete-account-btn"
          >
            Delete My Account & Data
          </button>
        </div>

        <Footer />
      </div>
    </div>
  );
};

// Membership Upgrade Screen
const MembershipUpgradeScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const benefits = [
    { icon: '&#128337;', title: 'Monthly Evolution Updates', desc: 'Upload new photos and track your transformation' },
    { icon: '&#127919;', title: 'Personalized Monthly Focus', desc: 'Fresh guidance tailored to your journey each month' },
    { icon: '&#128221;', title: 'New Embodiment Practices', desc: 'Journaling prompts, identity statements, action steps' },
    { icon: '&#128200;', title: 'Progress Tracking', desc: 'See how far you\'ve come with comparisons' }
  ];

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscriptions/create-checkout`, {
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error('Failed to start subscription');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pb-8 safe-area-bottom" data-testid="upgrade-screen">
      <div className="p-6 max-w-md mx-auto">
        <button onClick={() => navigate(-1)} className="text-[#C6A75E] mb-6" data-testid="back-btn">
          &#8592; Back
        </button>

        <div className="text-center mb-8">
          <Logo size="sm" />
          <h1 className="font-['Playfair_Display'] text-2xl text-gold-gradient mt-6">
            Your Evolution Is Ongoing
          </h1>
          <p className="text-[#888888] mt-4 leading-relaxed">
            This is not a report. This is your refinement system. Each month, you receive a new layer of clarity, guidance, and transformation.
          </p>
        </div>

        {/* Why This Matters */}
        <div className="luxury-card p-6 mb-8" data-testid="why-this-matters">
          <h3 className="text-[#C6A75E] font-['Playfair_Display'] text-lg mb-4">Why This Matters</h3>
          <div className="space-y-3">
            <p className="text-[#F5F5F5] text-sm leading-relaxed">Without guidance, most people stay stuck in guessing.</p>
            <p className="text-[#F5F5F5] text-sm leading-relaxed">With this system, you move forward with clarity.</p>
            <p className="text-[#F5F5F5] text-sm leading-relaxed">Small refinements create visible change.</p>
          </div>
        </div>

        <div className="space-y-4 mb-8">
          {benefits.map((b, i) => (
            <div key={i} className="luxury-card p-4 flex items-start gap-4 animate-slide-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <span className="text-[#C6A75E] text-2xl" dangerouslySetInnerHTML={{ __html: b.icon }} />
              <div>
                <p className="text-[#F5F5F5] font-medium">{b.title}</p>
                <p className="text-[#888888] text-sm mt-1">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-[#C6A75E] font-['Playfair_Display'] text-3xl mb-2">€19<span className="text-lg">/month</span></p>
          <p className="text-[#888888] text-sm mb-6">Cancel anytime</p>
          
          <button 
            onClick={handleSubscribe}
            className="btn-gold w-full"
            disabled={loading}
            data-testid="join-membership-btn"
          >
            {loading ? 'Processing...' : 'Join Membership'}
          </button>
        </div>

        <Footer />
      </div>
    </div>
  );
};

// Membership Dashboard
const MembershipDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [dashRes, progressRes] = await Promise.all([
          axios.get(`${API_URL}/api/membership/dashboard`),
          axios.get(`${API_URL}/api/membership/progress`).catch(() => ({ data: { has_comparison: false } }))
        ]);
        
        if (!dashRes.data.has_membership) {
          navigate('/membership/upgrade');
          return;
        }
        
        setData(dashRes.data);
        setProgress(progressRes.data);
      } catch (err) {
        toast.error('Failed to load membership data');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  if (loading) return <LoadingScreen />;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-black pb-8 safe-area-bottom" data-testid="membership-dashboard">
      {/* Header */}
      <div className="bg-[#111] p-6 border-b border-[rgba(198,167,94,0.2)]">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div>
            <p className="text-[#C6A75E] text-sm">&#10022; Member</p>
            <p className="text-[#F5F5F5] font-['Playfair_Display'] text-lg">Your Evolution Is Ongoing</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-[#C6A75E] text-sm">
            &#8592; Dashboard
          </button>
        </div>
      </div>

      <div className="p-6 max-w-2xl mx-auto">
        {/* Membership Philosophy */}
        <div className="mb-6 text-center">
          <p className="text-[#888888] text-sm leading-relaxed max-w-md mx-auto">
            This is not a report. This is your refinement system. Each month, you receive a new layer of clarity, guidance, and transformation.
          </p>
        </div>

        {/* Current Month Focus */}
        <div className="luxury-card p-6 mb-6 text-center bg-gradient-to-r from-[#1A1A1A] to-[#0F3B2E]/20">
          <p className="text-[#888888] text-xs uppercase tracking-wider mb-2">This Month's Focus</p>
          <p className="font-['Playfair_Display'] text-2xl text-[#C6A75E]">{data.current_focus}</p>
          <p className="text-[#888888] text-sm mt-2">
            Expires: {new Date(data.expires_at).toLocaleDateString()}
          </p>
        </div>

        {/* Upload New Photos */}
        {data.can_upload_this_month ? (
          <button 
            onClick={() => navigate('/membership/upload')}
            className="w-full luxury-card p-5 flex items-center justify-between mb-6 hover:border-[#C6A75E] transition-all"
            data-testid="upload-evolution-btn"
          >
            <div>
              <p className="text-[#F5F5F5] font-medium">Create Evolution Update</p>
              <p className="text-[#888888] text-sm mt-1">
                {data.reports_this_month}/2 reports this month
              </p>
            </div>
            <span className="text-[#C6A75E] text-2xl">+</span>
          </button>
        ) : (
          <div className="luxury-card p-5 mb-6 text-center">
            <p className="text-[#888888]">Monthly report limit reached</p>
            <p className="text-[#888888] text-sm">Come back next month for new updates</p>
          </div>
        )}

        {/* Before & After Comparison Card */}
        {progress?.has_comparison && (
          <div className="mb-6" data-testid="before-after-card">
            <h3 className="text-[#F5F5F5] font-['Playfair_Display'] mb-4">Your Evolution</h3>
            <div className="luxury-card p-6 bg-gradient-to-r from-[#1A1A1A] to-[#0F3B2E]/10">
              {/* Timeline Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#888888]"></div>
                  <span className="text-[#888888] text-xs">
                    {new Date(progress.previous.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <div className="flex-1 mx-4 h-px bg-gradient-to-r from-[#888888]/30 via-[#C6A75E]/50 to-[#C6A75E]"></div>
                <div className="flex items-center gap-2">
                  <span className="text-[#C6A75E] text-xs font-medium">
                    {new Date(progress.current.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                  <div className="w-2 h-2 rounded-full bg-[#C6A75E]"></div>
                </div>
              </div>

              {/* Comparison Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-[#888888] text-xs uppercase tracking-wider mb-2">Before</p>
                  <p className="text-[#F5F5F5] font-['Playfair_Display'] text-lg capitalize">{progress.previous.face_shape}</p>
                  {progress.previous.summary && (
                    <p className="text-[#888888] text-xs mt-2 leading-relaxed line-clamp-3">{progress.previous.summary.substring(0, 80)}...</p>
                  )}
                </div>
                <div className="text-center border-l border-[rgba(198,167,94,0.2)] pl-4">
                  <p className="text-[#C6A75E] text-xs uppercase tracking-wider mb-2">After</p>
                  <p className="text-[#F5F5F5] font-['Playfair_Display'] text-lg capitalize">{progress.current.face_shape}</p>
                  {progress.current.summary && (
                    <p className="text-[#888888] text-xs mt-2 leading-relaxed line-clamp-3">{progress.current.summary.substring(0, 80)}...</p>
                  )}
                </div>
              </div>

              {/* View Full Comparison */}
              <div className="mt-6 pt-4 border-t border-[rgba(198,167,94,0.1)] flex justify-between">
                <button
                  onClick={() => navigate(`/report/${progress.previous.id}`)}
                  className="text-[#888888] text-xs hover:text-[#C6A75E] transition-colors"
                >
                  View Previous
                </button>
                <button
                  onClick={() => navigate(`/report/${progress.current.id}`)}
                  className="text-[#C6A75E] text-xs font-medium"
                >
                  View Current &#8594;
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Past Reports */}
        <div>
          <h3 className="text-[#F5F5F5] font-['Playfair_Display'] mb-4">Report History</h3>
          <div className="space-y-3">
            {data.reports.map((report) => (
              <button
                key={report.id}
                onClick={() => navigate(`/report/${report.id}`)}
                className="w-full luxury-card p-4 text-left hover:border-[#C6A75E] transition-all"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#F5F5F5] capitalize">{report.preview?.face_shape || 'Analysis'}</p>
                    <p className="text-[#888888] text-xs mt-1">
                      {new Date(report.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-[#C6A75E] text-sm">View &#8594;</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

// Membership Photo Upload
const MembershipUploadScreen = () => {
  const navigate = useNavigate();
  
  // Reuse the guide screen flow but for membership
  return (
    <div className="min-h-screen bg-black flex flex-col p-6 safe-area-top safe-area-bottom" data-testid="membership-upload">
      <button onClick={() => navigate('/membership')} className="text-[#C6A75E] self-start mb-4">
        &#8592; Back
      </button>

      <div className="flex-1 flex flex-col items-center max-w-sm mx-auto w-full">
        <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] text-center">
          Monthly Evolution Update
        </h1>
        
        <p className="text-[#888888] text-center mt-4 text-sm">
          Upload new photos to see what has evolved, what remains strong, and where to focus your energy.
        </p>

        <div className="w-full mt-8 space-y-3">
          {[
            { icon: '&#9788;', text: 'Good natural lighting' },
            { icon: '&#10006;', text: 'No filters or edits' },
            { icon: '&#8635;', text: 'Hair away from face' },
            { icon: '&#9737;', text: 'Similar angle to previous photos' }
          ].map((tip, index) => (
            <div 
              key={index}
              className="luxury-card p-4 flex items-center gap-4 animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <span className="text-[#C6A75E] text-xl" dangerouslySetInnerHTML={{ __html: tip.icon }} />
              <span className="text-[#F5F5F5] text-sm">{tip.text}</span>
            </div>
          ))}
        </div>

        <div className="w-full mt-auto pt-8">
          <button 
            onClick={() => navigate('/membership/upload/front')}
            className="btn-gold w-full"
            data-testid="start-upload-btn"
          >
            Start Upload
          </button>
        </div>
      </div>
    </div>
  );
};

// Membership Review Screen - for evolution reports
const MembershipReviewScreen = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState({ front: null, side: null, smile: null });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const loadPhotos = async () => {
      const frontId = sessionStorage.getItem('front_photo_id');
      const sideId = sessionStorage.getItem('side_photo_id');
      const smileId = sessionStorage.getItem('smile_photo_id');

      const loaded = { front: null, side: null, smile: null };

      try {
        if (frontId) {
          const { data } = await axios.get(`${API_URL}/api/photos/${frontId}`);
          loaded.front = `data:image/jpeg;base64,${data.image_data}`;
        }
        if (sideId) {
          const { data } = await axios.get(`${API_URL}/api/photos/${sideId}`);
          loaded.side = `data:image/jpeg;base64,${data.image_data}`;
        }
        if (smileId) {
          const { data } = await axios.get(`${API_URL}/api/photos/${smileId}`);
          loaded.smile = `data:image/jpeg;base64,${data.image_data}`;
        }
      } catch (err) {
        console.error('Error loading photos:', err);
      }

      setPhotos(loaded);
      setLoading(false);
    };

    loadPhotos();
  }, []);

  const handleSubmit = async () => {
    const frontId = sessionStorage.getItem('front_photo_id');
    const sideId = sessionStorage.getItem('side_photo_id');
    const smileId = sessionStorage.getItem('smile_photo_id');

    if (!frontId || !sideId) {
      toast.error('Please upload required photos first');
      return;
    }

    setCreating(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/membership/evolution-report`, {
        front_photo_id: frontId,
        side_photo_id: sideId,
        smile_photo_id: smileId || null
      });

      // Clear session storage
      sessionStorage.removeItem('front_photo_id');
      sessionStorage.removeItem('side_photo_id');
      sessionStorage.removeItem('smile_photo_id');

      navigate(`/report/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create report');
      setCreating(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (creating) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
        <Logo size="md" />
        <div className="mt-8 animate-pulse-glow w-20 h-20 rounded-full border-2 border-[#C6A75E] flex items-center justify-center">
          <div className="spinner"></div>
        </div>
        <h2 className="font-['Playfair_Display'] text-xl text-[#F5F5F5] mt-8 text-center">
          Creating your Evolution Update...
        </h2>
        <p className="text-[#888888] text-sm mt-4 text-center max-w-xs">
          Analyzing changes and preparing your monthly insights.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col p-6 safe-area-top safe-area-bottom" data-testid="membership-review">
      <button onClick={() => navigate(-1)} className="text-[#C6A75E] self-start mb-4">
        &#8592; Back
      </button>

      <div className="flex-1 flex flex-col items-center max-w-sm mx-auto w-full">
        <h1 className="font-['Playfair_Display'] text-xl text-[#F5F5F5] text-center">
          Review Your Photos
        </h1>
        
        <p className="text-[#888888] text-center mt-2 text-sm">
          Ready to see your evolution?
        </p>

        <div className="grid grid-cols-3 gap-3 mt-8 w-full">
          {['front', 'side', 'smile'].map((type) => (
            <div key={type} className="relative">
              {photos[type] ? (
                <img 
                  src={photos[type]} 
                  alt={type} 
                  className="w-full aspect-square object-cover rounded-lg border border-[rgba(198,167,94,0.2)]"
                />
              ) : (
                <div className="w-full aspect-square bg-[#1A1A1A] rounded-lg border border-[rgba(198,167,94,0.2)] flex items-center justify-center">
                  <span className="text-[#888888] text-xs">{type === 'smile' ? 'Optional' : 'Missing'}</span>
                </div>
              )}
              <p className="text-[#888888] text-xs text-center mt-2 capitalize">{type}</p>
            </div>
          ))}
        </div>

        <div className="w-full mt-auto pt-8 space-y-3">
          <button 
            onClick={handleSubmit}
            className="btn-gold w-full"
            disabled={!photos.front || !photos.side}
          >
            Create Evolution Update
          </button>
          
          <button 
            onClick={() => navigate('/membership/upload')}
            className="btn-outline w-full"
          >
            Retake Photos
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin Panel
const AdminPanel = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [payments, setPayments] = useState([]);
  const [emailCaptures, setEmailCaptures] = useState([]);
  const [subscribers, setSubscribers] = useState({ subscribers: [], total_revenue: 0, active_subscribers: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [usersRes, reportsRes, paymentsRes, emailsRes, subsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/users`),
          axios.get(`${API_URL}/api/admin/reports`),
          axios.get(`${API_URL}/api/admin/payments`),
          axios.get(`${API_URL}/api/admin/email-captures`),
          axios.get(`${API_URL}/api/admin/subscribers`)
        ]);
        setUsers(usersRes.data);
        setReports(reportsRes.data);
        setPayments(paymentsRes.data);
        setEmailCaptures(emailsRes.data);
        setSubscribers(subsRes.data);
      } catch (err) {
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Users exported!');
    } catch (err) {
      toast.error('Failed to export users');
    }
  };

  const handleExportEmails = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/email-captures/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'email-captures.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Emails exported!');
    } catch (err) {
      toast.error('Failed to export emails');
    }
  };

  const handleExportSubscribers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/subscribers/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscribers-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Subscribers exported!');
    } catch (err) {
      toast.error('Failed to export subscribers');
    }
  };

  const handleRegenerateReport = async (reportId) => {
    try {
      await axios.post(`${API_URL}/api/admin/reports/${reportId}/regenerate`);
      toast.success('Report regenerated!');
    } catch (err) {
      toast.error('Failed to regenerate report');
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-black pb-8" data-testid="admin-panel">
      {/* Header */}
      <div className="bg-[#111] p-6 border-b border-[rgba(198,167,94,0.2)]">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <h1 className="text-[#F5F5F5] font-['Playfair_Display'] text-xl">Admin Panel</h1>
          <button onClick={() => navigate('/dashboard')} className="text-[#C6A75E] text-sm" data-testid="back-btn">
            &#8592; Dashboard
          </button>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-[rgba(198,167,94,0.2)] pb-4 overflow-x-auto">
          {['users', 'reports', 'payments', 'subscribers', 'emails'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm capitalize whitespace-nowrap ${
                tab === t ? 'bg-[#C6A75E] text-black' : 'text-[#888888]'
              }`}
              data-testid={`tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Users Tab */}
        {tab === 'users' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-[#888888]">{users.length} users</p>
              <button onClick={handleExportUsers} className="btn-outline text-sm py-2" data-testid="export-btn">
                Export CSV
              </button>
            </div>
            
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="luxury-card p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[#F5F5F5]">{user.email}</p>
                      <p className="text-[#888888] text-sm">{user.name} - {user.role}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#C6A75E] text-sm">{user.report_count} reports</p>
                      <p className="text-[#888888] text-xs">{new Date(user.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {tab === 'reports' && (
          <div className="space-y-3">
            {reports.map((report) => (
              <div key={report.id} className="luxury-card p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[#F5F5F5]">{report.user_email}</p>
                    <p className="text-[#888888] text-sm">
                      Status: {report.status} | {report.is_paid ? 'Paid' : 'Unpaid'}
                    </p>
                  </div>
                  <div className="text-right">
                    <button 
                      onClick={() => handleRegenerateReport(report.id)}
                      className="text-[#C6A75E] text-sm underline"
                      data-testid={`regenerate-${report.id}`}
                    >
                      Regenerate
                    </button>
                    <p className="text-[#888888] text-xs mt-1">{new Date(report.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Payments Tab */}
        {tab === 'payments' && (
          <div className="space-y-3">
            {payments.length === 0 ? (
              <p className="text-[#888888] text-center py-8">No payments yet</p>
            ) : (
              payments.map((payment) => (
                <div key={payment.id} className="luxury-card p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[#F5F5F5]">{payment.user_email}</p>
                      <p className="text-[#888888] text-sm">
                        €{payment.amount} {payment.currency?.toUpperCase() || 'EUR'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm ${payment.payment_status === 'paid' ? 'text-green-400' : 'text-[#888888]'}`}>
                        {payment.payment_status}
                      </p>
                      <p className="text-[#888888] text-xs">{new Date(payment.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Subscribers Tab */}
        {tab === 'subscribers' && (
          <div>
            {/* Revenue Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="luxury-card p-4 text-center">
                <p className="text-[#888888] text-xs mb-1">Total Revenue</p>
                <p className="text-[#C6A75E] font-['Playfair_Display'] text-2xl">€{subscribers.total_revenue}</p>
              </div>
              <div className="luxury-card p-4 text-center">
                <p className="text-[#888888] text-xs mb-1">Active Members</p>
                <p className="text-[#C6A75E] font-['Playfair_Display'] text-2xl">{subscribers.active_subscribers}</p>
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <p className="text-[#888888]">{subscribers.subscribers?.length || 0} total subscriptions</p>
              <button onClick={handleExportSubscribers} className="btn-outline text-sm py-2" data-testid="export-subscribers-btn">
                Export CSV
              </button>
            </div>
            
            <div className="space-y-3">
              {(!subscribers.subscribers || subscribers.subscribers.length === 0) ? (
                <p className="text-[#888888] text-center py-8">No subscribers yet</p>
              ) : (
                subscribers.subscribers.map((sub) => (
                  <div key={sub.id} className="luxury-card p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[#F5F5F5]">{sub.user_email}</p>
                        <p className="text-[#888888] text-sm">€{sub.amount}/month</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm ${sub.status === 'active' ? 'text-green-400' : 'text-[#888888]'}`}>
                          {sub.status}
                        </p>
                        <p className="text-[#888888] text-xs">
                          {sub.expires_at ? `Expires: ${new Date(sub.expires_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Emails Tab */}
        {tab === 'emails' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-[#888888]">{emailCaptures.length} captured emails</p>
              <button onClick={handleExportEmails} className="btn-outline text-sm py-2" data-testid="export-emails-btn">
                Export CSV
              </button>
            </div>
            
            <div className="space-y-3">
              {emailCaptures.length === 0 ? (
                <p className="text-[#888888] text-center py-8">No captured emails yet</p>
              ) : (
                emailCaptures.map((capture, idx) => (
                  <div key={idx} className="luxury-card p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[#F5F5F5]">{capture.email}</p>
                        <p className="text-[#888888] text-sm">Source: {capture.source}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#888888] text-xs">{new Date(capture.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Google Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        await loginWithGoogle(sessionId);
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('Google auth error:', err);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, loginWithGoogle]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <div className="spinner mx-auto mb-4"></div>
        <p className="text-[#888888]">Completing sign in...</p>
      </div>
    </div>
  );
};

// Email Capture Popup
const EmailCapturePopup = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/email-capture`, { email, source: 'popup' });
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="email-popup">
      <div className="luxury-card p-8 max-w-md w-full relative animate-slide-up">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-[#888888] hover:text-[#F5F5F5] text-xl"
          data-testid="close-popup"
        >
          &times;
        </button>

        {submitted ? (
          <div className="text-center py-8">
            <div className="text-[#C6A75E] text-4xl mb-4">&#10003;</div>
            <h3 className="font-['Playfair_Display'] text-xl text-[#F5F5F5] mb-2">Welcome!</h3>
            <p className="text-[#888888] text-sm">Check your inbox for a special welcome message.</p>
          </div>
        ) : (
          <>
            <Logo size="sm" />
            <h3 className="font-['Playfair_Display'] text-xl text-[#F5F5F5] mt-4 text-center">
              Discover Your Natural Elegance
            </h3>
            <p className="text-[#888888] text-sm text-center mt-2">
              Get exclusive insights on facial harmony and self-enhancement delivered to your inbox.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="luxury-input"
                required
                data-testid="popup-email-input"
              />
              <button 
                type="submit"
                className="btn-gold w-full"
                disabled={loading}
                data-testid="popup-submit"
              >
                {loading ? 'Joining...' : 'Get Started'}
              </button>
            </form>

            <p className="text-[#888888] text-xs text-center mt-4">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// Subscription Success Screen
const SubscriptionSuccessScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      if (attempts >= 10) {
        setStatus('timeout');
        return;
      }

      try {
        const { data } = await axios.get(`${API_URL}/api/subscriptions/status/${sessionId}`);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
        } else if (data.status === 'expired') {
          setStatus('failed');
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (err) {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    };

    pollStatus();
  }, [searchParams, navigate]);

  if (status === 'verifying') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
        <div className="spinner mb-4"></div>
        <p className="text-[#F5F5F5]">Activating your subscription...</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="subscription-success">
        <div className="text-[#C6A75E] text-6xl mb-6">&#10003;</div>
        <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] mb-4">Subscription Active!</h1>
        <p className="text-[#888888] text-center mb-8 max-w-sm">
          You now have unlimited access to create blueprints for the next 30 days.
        </p>
        
        <button onClick={() => navigate('/dashboard')} className="btn-gold" data-testid="go-dashboard">
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
      <div className="text-red-400 text-6xl mb-6">&#10007;</div>
      <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F5] mb-4">Subscription Issue</h1>
      <p className="text-[#888888] text-center mb-8">There was an issue activating your subscription.</p>
      
      <button onClick={() => navigate('/dashboard')} className="btn-gold">
        Go to Dashboard
      </button>
    </div>
  );
};

// Main App
function App() {
  const [showEmailPopup, setShowEmailPopup] = useState(false);

  useEffect(() => {
    // Show popup after 10 seconds if user hasn't signed up
    const timer = setTimeout(() => {
      const hasSeenPopup = localStorage.getItem('emailPopupSeen');
      if (!hasSeenPopup) {
        setShowEmailPopup(true);
        localStorage.setItem('emailPopupSeen', 'true');
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: '#1A1A1A',
              color: '#F5F5F5',
              border: '1px solid rgba(198, 167, 94, 0.2)'
            }
          }}
        />
        {showEmailPopup && <EmailCapturePopup onClose={() => setShowEmailPopup(false)} />}
        <CookieNotice />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

// App Routes - Handles OAuth callback detection
const AppRoutes = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<SplashScreen />} />
      <Route path="/welcome" element={<WelcomeScreen />} />
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/register" element={<RegisterScreen />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      
      <Route path="/guide" element={
        <ProtectedRoute><PhotoGuideScreen /></ProtectedRoute>
      } />
      <Route path="/upload/front" element={
        <ProtectedRoute>
          <PhotoUploadScreen type="front" step={1} nextRoute="/upload/side" />
        </ProtectedRoute>
      } />
      <Route path="/upload/side" element={
        <ProtectedRoute>
          <PhotoUploadScreen type="side" step={2} nextRoute="/upload/smile" />
        </ProtectedRoute>
      } />
      <Route path="/upload/smile" element={
        <ProtectedRoute>
          <PhotoUploadScreen type="smile" step={3} nextRoute="/upload/review" />
        </ProtectedRoute>
      } />
      <Route path="/upload/review" element={
        <ProtectedRoute><UploadReviewScreen /></ProtectedRoute>
      } />
      <Route path="/processing" element={
        <ProtectedRoute><ProcessingScreen /></ProtectedRoute>
      } />
      <Route path="/report/:reportId/preview" element={
        <ProtectedRoute><ReportPreviewScreen /></ProtectedRoute>
      } />
      <Route path="/report/:reportId" element={
        <ProtectedRoute><FullReportScreen /></ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute><PaymentSuccessScreen /></ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute><SubscriptionSuccessScreen /></ProtectedRoute>
      } />
      <Route path="/membership" element={
        <ProtectedRoute><MembershipDashboard /></ProtectedRoute>
      } />
      <Route path="/membership/upgrade" element={
        <ProtectedRoute><MembershipUpgradeScreen /></ProtectedRoute>
      } />
      <Route path="/membership/upload" element={
        <ProtectedRoute><MembershipUploadScreen /></ProtectedRoute>
      } />
      <Route path="/membership/upload/front" element={
        <ProtectedRoute>
          <PhotoUploadScreen type="front" step={1} nextRoute="/membership/upload/side" />
        </ProtectedRoute>
      } />
      <Route path="/membership/upload/side" element={
        <ProtectedRoute>
          <PhotoUploadScreen type="side" step={2} nextRoute="/membership/upload/smile" />
        </ProtectedRoute>
      } />
      <Route path="/membership/upload/smile" element={
        <ProtectedRoute>
          <PhotoUploadScreen type="smile" step={3} nextRoute="/membership/upload/review" />
        </ProtectedRoute>
      } />
      <Route path="/membership/upload/review" element={
        <ProtectedRoute><MembershipReviewScreen /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardScreen /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <AdminRoute><AdminPanel /></AdminRoute>
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
